import React, { createContext, useContext, useEffect, useState } from 'react';
import { DICTIONARIES, LANGUES_DISPONIBLES } from './index';
import { DEFAULT_LANG } from './languages';

/**
 * i18n maison, sans dépendance.
 *
 * Principes :
 * - le FRANÇAIS est la langue source : ses textes vivent dans src/i18n/fr/
 *   et doivent rester identiques au site d'avant la traduction.
 * - t('cle') rend la traduction de la langue active, sinon le français,
 *   sinon la clé elle-même (jamais d'écran vide).
 * - les textes PRODUIT (nom, description, features des packs) viennent de la
 *   BASE via l'API et sont gérés par l'owner dans Admin → Stocks : en français
 *   on affiche toujours la valeur de la base, et les autres langues utilisent
 *   les clés packs.<id>.<champ> avec repli sur la base. Ne jamais faire
 *   l'inverse, sinon une modification faite dans l'admin cesserait d'être
 *   visible.
 */

const STORAGE_KEY = 'mirum-lang';

/** Une langue n'est retenue que si elle est réellement traduite. */
const estDisponible = (code) => LANGUES_DISPONIBLES.some((l) => l.code === code);

/**
 * Langue demandee par l'adresse : /shop?lang=nl.
 *
 * C'est le SEUL moyen pour une page statique (/nl/index.html n'a pas une ligne
 * de JavaScript) ou un lien partage de transmettre une langue. Sans cela, un
 * neerlandophone qui vient de lire /nl/ en entier clique « Winkel » et retombe
 * en francais si son telephone est regle en francais — cas banal a Bruxelles.
 *
 * On lit window.location et non useSearchParams : le LanguageProvider est
 * monte HORS du <Router>, les hooks de react-router n'y sont pas disponibles
 * et l'appel leverait au demarrage.
 *
 * Fonction PURE : elle n'ecrit rien. La persistance se fait dans un effet plus
 * bas — ecrire ici serait un effet de bord pendant le rendu, rejoue par
 * <StrictMode>, et cela briserait l'invariant sur lequel repose tout le reste :
 * une valeur dans localStorage est TOUJOURS un choix delibere.
 */
function langDeLURL() {
  try {
    const brut = new URLSearchParams(window.location.search).get('lang') || '';
    const code = brut.slice(0, 2).toLowerCase();
    return estDisponible(code) ? code : null;
  } catch {
    return null;
  }
}

function detectLang() {
  const demandee = langDeLURL();
  if (demandee) return demandee;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && estDisponible(saved)) return saved;
  } catch {
    // localStorage indisponible (navigation privée stricte) : on détecte.
  }
  // On teste la disponibilité, pas la simple existence du dictionnaire : sinon
  // un navigateur réglé en allemand basculait sur un dictionnaire vide et
  // affichait du français sous une étiquette « de ».
  const nav = (navigator.language || DEFAULT_LANG).slice(0, 2).toLowerCase();
  return estDisponible(nav) ? nav : DEFAULT_LANG;
}

const LanguageContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (key) => key,
  tp: (pack, field, dbValue) => dbValue,
  tpList: (pack, field, dbList) => dbList,
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(detectLang);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Ouvrir un lien portant ?lang=nl EST un choix explicite : on le retient,
  // comme un clic sur le selecteur. Ici et jamais dans l'initialiseur de
  // useState, pour les raisons expliquees au-dessus de langDeLURL.
  useEffect(() => {
    const demandee = langDeLURL();
    if (!demandee) return;
    try {
      localStorage.setItem(STORAGE_KEY, demandee);
    } catch {
      // tant pis, le choix ne survivra pas au rechargement
    }
  }, []);

  const setLang = (code) => {
    if (!estDisponible(code)) return;
    setLangState(code);
    try {
      localStorage.setItem(STORAGE_KEY, code);
    } catch {
      // tant pis, le choix ne survivra pas au rechargement
    }
  };

  /** Texte d'interface. Variables : t('x', { n: 3 }) remplace {n} dans la valeur. */
  const t = (key, vars) => {
    let s = DICTIONARIES[lang]?.[key] ?? DICTIONARIES[DEFAULT_LANG][key] ?? key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        s = s.split(`{${k}}`).join(String(v));
      }
    }
    return s;
  };

  /** Champ produit : la base en français, la traduction sinon (repli = base). */
  const tp = (pack, field, dbValue) => {
    if (lang === DEFAULT_LANG || !pack?.id) return dbValue;
    return DICTIONARIES[lang]?.[`packs.${pack.id}.${field}`] ?? dbValue;
  };

  /** Liste produit (features) : traduite = une seule valeur séparée par des '|'. */
  const tpList = (pack, field, dbList) => {
    if (lang === DEFAULT_LANG || !pack?.id) return dbList;
    const v = DICTIONARIES[lang]?.[`packs.${pack.id}.${field}`];
    return typeof v === 'string' && v.length > 0 ? v.split('|') : dbList;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t, tp, tpList }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  return useContext(LanguageContext);
}
